import { useAccessibility } from "../../context/AccessibilityContext/AccessibilityContext";
import Modal from "../Modal/Modal";
import style from './teammatemodal.module.scss';
import { useState, useRef, useEffect, useMemo } from 'react';

const TeammateModal = ({ teammate, onClose }) => {
    const { settings: { contrast } } = useAccessibility();
    const [imageOrientation, setImageOrientation] = useState('unknown');
    const [imageLoaded, setImageLoaded] = useState(false);
    const imgRef = useRef(null);

    // Function to detect image orientation
    const detectOrientation = useMemo(() => (img) => {
        if (!img) return 'unknown';
        const width = img.naturalWidth || img.width;
        const height = img.naturalHeight || img.height;
        const ratio = width / height;
        
        if (ratio > 1.2) {
            return 'landscape';
        } else if (ratio < 0.8) {
            return 'portrait';
        } else {
            return 'square';
        }
    }, []);

    // Prepare teammate data with fallbacks
    const teammateData = useMemo(() => {
        if (!teammate) return null;
        
        return {
            // Name
            fullName: teammate.name || 
                     (teammate.LastName && teammate.FirstName && teammate.PatronymicName 
                        ? `${teammate.LastName} ${teammate.FirstName} ${teammate.PatronymicName}`
                        : teammate.Name || 'Сотрудник'),
            lastName: teammate.LastName || teammate.lastName || '',
            firstName: teammate.FirstName || teammate.firstName || '',
            patronymicName: teammate.PatronymicName || teammate.patronymicName || '',
            
            // Professional info
            position: teammate.position || teammate.Position || '',
            sport: teammate.sport?.data?.attributes?.Title || teammate.sport || teammate.Sport || '',
            achievements: teammate.Achievements || teammate.achievements || '',
            experience: teammate.Experience || teammate.experience || '',
            
            // Education and specialization
            education: teammate.education || teammate.Education || '',
            specialization: teammate.specialization || teammate.Specialization || '',
            
            // Description and additional info
            description: teammate.description || teammate.Description || teammate.bio || teammate.Bio || '',
            additionalAchievements: Array.isArray(teammate.additionalAchievements || teammate.AdditionalAchievements) 
                ? teammate.additionalAchievements || teammate.AdditionalAchievements 
                : [],
            
            // Image
            image: teammate.Image?.data?.attributes?.url || teammate.image || null,
            
            // Other
            createdAt: teammate.createdAt,
            updatedAt: teammate.updatedAt
        };
    }, [teammate]);

    const imageUrl = useMemo(() => {
        return teammateData?.image ? `${teammateData.image}` : null;
    }, [teammateData]);

    // Handle image load
    const handleImageLoad = (e) => {
        const img = e.target;
        const orientation = detectOrientation(img);
        setImageOrientation(orientation);
        setImageLoaded(true);
    };

    // Handle image error
    const handleImageError = (e) => {
        console.error('Image failed to load in modal:', imageUrl);
        e.target.style.display = 'none';
        const parent = e.target.parentNode;
        if (parent) {
            parent.innerHTML = `
                <div class="${style.imagePlaceholder}">
                    <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                </div>
            `;
        }
        setImageLoaded(true);
    };

    // Preload image to detect orientation
    useEffect(() => {
        if (!imageUrl) {
            setImageOrientation('unknown');
            setImageLoaded(true);
            return;
        }

        const img = new Image();
        img.src = imageUrl;
        
        img.onload = () => {
            const orientation = detectOrientation(img);
            setImageOrientation(orientation);
            setImageLoaded(true);
        };
        
        img.onerror = () => {
            setImageOrientation('unknown');
            setImageLoaded(true);
        };
    }, [imageUrl, detectOrientation]);

    if (!teammate) return null;

    // Accessibility-aware colors
    const backgroundColor = contrast === 'default' ? '#fff' : 'var(--bg-primary)';
    const textColor = contrast === 'default' ? '#231f20' : 'var(--text-primary)';
    const accentColor = contrast === 'default' ? '#1d3e8c' : 'var(--accent-color)';
    const borderColor = contrast === 'default' ? '#e5e5e5' : 'var(--border-color)';

    // Get image style based on orientation
    const getImageStyle = () => {
        switch(imageOrientation) {
            case 'portrait':
                return {
                    objectFit: 'contain',
                    objectPosition: 'top center',
                    width: 'auto',
                    height: '100%',
                    maxWidth: '100%'
                };
            case 'landscape':
                return {
                    objectFit: 'cover',
                    objectPosition: 'center center'
                };
            case 'square':
                return {
                    objectFit: 'cover',
                    objectPosition: 'center center'
                };
            default:
                return {
                    objectFit: 'cover',
                    objectPosition: 'center center'
                };
        }
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('ru-RU', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        } catch (error) {
            return '';
        }
    };

    // Render paragraph content
    const renderParagraphs = (content) => {
        if (!content) return null;
        
        if (Array.isArray(content)) {
            return content
                .filter(paragraph => paragraph && paragraph.trim())
                .map((paragraph, index) => (
                    <p key={index} className={style.textParagraph}>
                        {paragraph}
                    </p>
                ));
        }

        if (typeof content === 'string') {
            const paragraphs = content.split('\n').filter(para => para.trim());
            if (paragraphs.length === 0) return null;
            
            return paragraphs.map((paragraph, index) => (
                <p key={index} className={style.textParagraph}>
                    {paragraph}
                </p>
            ));
        }

        return null;
    };

    return (
        <Modal
            active={!!teammate}
            setActive={onClose}
            width="90%"
            maxWidth="800px"
            height="90vh"
            maxHeight="90vh"
            padding="0"
        >
            <div 
                className={style.teammateModalContent}
                style={{ 
                    background: backgroundColor,
                    color: textColor,
                    width: '100%',
                    height: '100%'
                }}
            >
                {/* Close Button */}
                <button 
                    className={style.closeButton}
                    onClick={onClose}
                    aria-label="Закрыть"
                    style={{
                        color: textColor,
                        border: `2px solid ${borderColor}`,
                        background: backgroundColor
                    }}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>

                {/* Main Content Container */}
                <div className={style.modalContainer}>
                    {/* Header Section with Photo */}
                    <div className={style.headerSection}>
                        {imageUrl ? (
                            <div 
                                className={`${style.imageWrapper} ${imageLoaded ? style[imageOrientation + 'Image'] : ''}`}
                                data-orientation={imageOrientation}
                            >
                                <img 
                                    ref={imgRef}
                                    src={imageUrl} 
                                    alt={teammateData.fullName} 
                                    className={style.headerImage}
                                    style={getImageStyle()}
                                    onLoad={handleImageLoad}
                                    onError={handleImageError}
                                    loading="lazy"
                                />
                                {!imageLoaded && (
                                    <div className={style.imageLoader}>
                                        <div className={style.loaderSpinner}></div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className={style.imagePlaceholder}>
                                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="12" cy="7" r="4"></circle>
                                </svg>
                            </div>
                        )}
                        
                        {/* Name and Position */}
                        <div className={style.headerInfo}>
                            <h1 className={style.name}>
                                {teammateData.fullName}
                            </h1>
                            {teammateData.position && (
                                <div className={style.position}>
                                    {teammateData.position}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Content Sections */}
                    <div className={style.contentSections}>
                        {/* Sport Section */}
                        {teammateData.sport && (
                            <section className={style.infoSection}>
                                <h2 className={style.sectionTitle}>Вид спорта</h2>
                                <div className={style.sectionContent}>
                                    <p className={style.sectionText}>{teammateData.sport}</p>
                                </div>
                            </section>
                        )}

                        {/* Achievements Section */}
                        {teammateData.achievements && (
                            <section className={style.infoSection}>
                                <h2 className={style.sectionTitle}>Звания и награды</h2>
                                <div className={style.sectionContent}>
                                    <p className={style.sectionText}>{teammateData.achievements}</p>
                                </div>
                            </section>
                        )}

                        {/* Experience Section */}
                        {teammateData.experience && (
                            <section className={style.infoSection}>
                                <h2 className={style.sectionTitle}>Опыт работы</h2>
                                <div className={style.sectionContent}>
                                    <p className={style.sectionText}>{teammateData.experience}</p>
                                </div>
                            </section>
                        )}

                        {/* Education Section */}
                        {teammateData.education && (
                            <section className={style.infoSection}>
                                <h2 className={style.sectionTitle}>Образование</h2>
                                <div className={style.sectionContent}>
                                    <p className={style.sectionText}>{teammateData.education}</p>
                                </div>
                            </section>
                        )}

                        {/* Specialization Section */}
                        {teammateData.specialization && (
                            <section className={style.infoSection}>
                                <h2 className={style.sectionTitle}>Специализация</h2>
                                <div className={style.sectionContent}>
                                    <p className={style.sectionText}>{teammateData.specialization}</p>
                                </div>
                            </section>
                        )}

                        {/* Description Section */}
                        {teammateData.description && (
                            <section className={style.infoSection}>
                                <h2 className={style.sectionTitle}>О сотруднике</h2>
                                <div className={style.sectionContent}>
                                    {renderParagraphs(teammateData.description)}
                                </div>
                            </section>
                        )}

                        {/* Additional Achievements Section */}
                        {teammateData.additionalAchievements && teammateData.additionalAchievements.length > 0 && (
                            <section className={style.infoSection}>
                                <h2 className={style.sectionTitle}>Достижения</h2>
                                <div className={style.sectionContent}>
                                    <ul className={style.achievementsList}>
                                        {teammateData.additionalAchievements
                                            .filter(item => item && item.trim())
                                            .map((achievement, index) => (
                                                <li key={index} className={style.achievementItem}>
                                                    {achievement}
                                                </li>
                                            ))
                                        }
                                    </ul>
                                </div>
                            </section>
                        )}
                    </div>

                    {/* Footer */}
                    <div className={style.footer}>
                        {(teammateData.createdAt || teammateData.updatedAt) && (
                            <div className={style.metaInfo}>
                                {teammateData.createdAt && (
                                    <span className={style.metaItem}>
                                        Добавлено: {formatDate(teammateData.createdAt)}
                                    </span>
                                )}
                                {teammateData.updatedAt && teammateData.updatedAt !== teammateData.createdAt && (
                                    <span className={style.metaItem}>
                                        Обновлено: {formatDate(teammateData.updatedAt)}
                                    </span>
                                )}
                            </div>
                        )}
                        
                        <button 
                            className={style.closeActionButton}
                            onClick={onClose}
                            style={{
                                background: accentColor,
                                color: '#fff'
                            }}
                        >
                            Закрыть
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default TeammateModal;